import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Pencil, Save, X } from 'lucide-react'

import { type ClientProfile } from '@/hooks/use-client-profile'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

interface GeneralInformationProps {
    clientProfile: ClientProfile
    onSave: (updatedDetails: Partial<ClientProfile>) => Promise<void>
}

export function GeneralInformation({ clientProfile, onSave }: GeneralInformationProps) {
    const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false)
    const [editedClientDetails, setEditedClientDetails] = useState<ClientProfile>(clientProfile)
    const [isLoading, setIsLoading] = useState(false)
    const { t } = useTranslation(undefined, { keyPrefix: 'externalClient' })

    const handleSaveClientDetails = async () => {
        try {
            setIsLoading(true)
            await onSave({
                name: editedClientDetails.name,
                phone: editedClientDetails.phone,
                email: editedClientDetails.email,
                address: editedClientDetails.address,
            })

            setIsEditClientModalOpen(false)
        } catch (error) {
            console.error('Error updating client details:', error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>{t('generalInfo', 'General information')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='space-y-4'>
                        <div className='grid grid-cols-[100px_1fr] gap-2'>
                            <span className='text-muted-foreground'>{t('name', 'Name')}:</span>
                            <span>{clientProfile.name}</span>
                        </div>
                        <div className='grid grid-cols-[100px_1fr] gap-2'>
                            <span className='text-muted-foreground'>{t('phone', 'Phone')}:</span>
                            <span>{clientProfile.phone}</span>
                        </div>
                        <div className='grid grid-cols-[100px_1fr] gap-2'>
                            <span className='text-muted-foreground'>{t('email', 'Email')}:</span>
                            <span>{clientProfile.email}</span>
                        </div>
                        <div className='grid grid-cols-[100px_1fr] gap-2'>
                            <span className='text-muted-foreground'>{t('address', 'Address')}:</span>
                            <span>{clientProfile.address}</span>
                        </div>
                        <div className='flex justify-end sm:justify-end'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setIsEditClientModalOpen(true)}
                                className='w-full sm:w-auto'
                            >
                                <Pencil className='mr-2 h-4 w-4' />
                                {t('edit', 'Edit')}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={isEditClientModalOpen} onOpenChange={setIsEditClientModalOpen}>
                <DialogContent className='w-[95vw] max-w-[425px] p-3'>
                    <DialogHeader className='pb-2'>
                        <DialogTitle className='flex items-center justify-between text-base font-medium'>
                            {t('editProfile', 'Edit Profile')}
                            <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => setIsEditClientModalOpen(false)}
                                className='h-6 w-6 p-0'
                                disabled={isLoading}
                            >
                                <X className='h-4 w-4' />
                            </Button>
                        </DialogTitle>
                    </DialogHeader>
                    <div className='grid gap-2 py-2'>
                        <div>
                            <Label className='text-xs'>{t('name', 'Name')}</Label>
                            <Input
                                value={editedClientDetails.name}
                                onChange={e =>
                                    setEditedClientDetails(prev => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                                className='h-8 text-sm'
                                disabled={isLoading}
                            />
                        </div>
                        <div className='grid grid-cols-2 gap-2'>
                            <div>
                                <Label className='text-xs'>{t('phone', 'Phone')}</Label>
                                <Input
                                    value={editedClientDetails.phone}
                                    onChange={e =>
                                        setEditedClientDetails(prev => ({
                                            ...prev,
                                            phone: e.target.value,
                                        }))
                                    }
                                    className='h-8 text-sm'
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <Label className='text-xs'>{t('email', 'Email')}</Label>
                                <Input
                                    value={editedClientDetails.email}
                                    onChange={e =>
                                        setEditedClientDetails(prev => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    className='h-8 text-sm'
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div>
                            <Label className='text-xs'>{t('address', 'Address')}</Label>
                            <Input
                                value={editedClientDetails.address}
                                onChange={e =>
                                    setEditedClientDetails(prev => ({
                                        ...prev,
                                        address: e.target.value,
                                    }))
                                }
                                className='h-8 text-sm'
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <DialogFooter className='flex gap-2 pt-2'>
                        <Button
                            onClick={handleSaveClientDetails}
                            className='h-8 flex-1 text-xs font-medium'
                            disabled={isLoading}
                        >
                            <Save className='mr-2 h-4 w-4' />
                            {t('save', 'Save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
